import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapPin, Plus, X } from 'lucide-react';
import {
  Input,
  Label,
  Button,
  Card,
  CardContent,
  CardHeader,
  VirtualizedFormSelect,
  ConfirmationDialog,
} from '@roger/ui-components';
import {
  getAvailableClinicians,
  getCurrentEpisode,
  updateCurrentEpisodeCaseManager,
  updateCurrentEpisodeClinicalManager,
  updateCurrentEpisodeDisasterTriageCodesPatientsPatientIdCurrentEpisodeDisasterTriageCodesPatch,
  updateEpisodeAssignments,
  syncEpisodeNoaPhysician,
  checkEpisodeNoaPhysicianSync,
  listAgencyClinicPhysicians,
} from '../../services/api';
import type {
  PatientResponse,
  AvailableClinicianResponse,
  ClinicPhysicianResponse,
} from '../../services/api/types.gen';
import { formatPhysicianName } from '../../utils/formatting';
import PhysicianInfoPopover from '../PhysicianInfoPopover';
import { ClinicianSelect } from '../clinician/ClinicianSelect';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { PatientImportBadgeToggle } from './PatientImportBadgeToggle';
import { PatientTestStatusToggle } from './PatientTestStatusToggle';

const MAX_ASSOCIATED_PHYSICIANS = 10;

interface PatientEpisodeFlagsSectionProps {
  patient: PatientResponse;
  canEditPatientInfo?: boolean;
  onPatientUpdated: (patient: PatientResponse) => void;
  /** Fired after a successful primary or associated physician assignment save. */
  onEpisodeAssignmentsChanged?: () => void;
}

type DisasterTriageCodesPatchBody = {
  triage_code?: string | null;
  disaster_code?: string | null;
  reset_triage_code_to_oasis?: boolean;
  reset_disaster_code_to_oasis?: boolean;
};

type DisasterTriageCodesSnapshot = {
  patientId: string;
  episodeId: string;
  triageCode: string;
  disasterCode: string;
  triageCodeIsManual: boolean;
  disasterCodeIsManual: boolean;
  body: DisasterTriageCodesPatchBody;
};

// Current-episode assignments (case manager, physician, dates) and the
// imported / test-patient flags. These are saved on change against the
// episode, not through the patient form's debounced autosave, so the state
// lives here rather than in PatientInfoTab.
export const PatientEpisodeFlagsSection: React.FC<
  PatientEpisodeFlagsSectionProps
> = ({
  patient,
  canEditPatientInfo = true,
  onPatientUpdated,
  onEpisodeAssignmentsChanged,
}) => {
  const { error: showError, success: showSuccess } = useToast();

  const [caseManagers, setCaseManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [currentCaseManagerId, setCurrentCaseManagerId] = useState<
    string | null
  >(null);
  const [clinicalManagers, setClinicalManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [currentClinicalManagerId, setCurrentClinicalManagerId] = useState<
    string | null
  >(null);
  const confirmedClinicalManagerIdRef = useRef<string | null>(null);
  const clinicalManagerRequestRef = useRef(0);
  // Full-list replaces must not overlap: each save is queued behind the prior
  // one and controls stay disabled while any write is in flight, so the server
  // and UI cannot diverge when requests would otherwise commit out of order.
  const confirmedAssociatedPhysicianIdsRef = useRef<string[]>([]);
  const associatedSaveTailRef = useRef(Promise.resolve());
  const associatedSavePendingCountRef = useRef(0);
  const [associatedSavePending, setAssociatedSavePending] = useState(false);
  // Bumped on patient change so a queued save for chart A cannot write into B.
  const chartGenerationRef = useRef(0);
  const [episodeStartDate, setEpisodeStartDate] = useState<string | null>(null);
  const [episodeEndDate, setEpisodeEndDate] = useState<string | null>(null);
  const [episodeTriageCode, setEpisodeTriageCode] = useState('');
  const [episodeDisasterCode, setEpisodeDisasterCode] = useState('');
  const [episodeTriageCodeIsManual, setEpisodeTriageCodeIsManual] =
    useState(false);
  const [episodeDisasterCodeIsManual, setEpisodeDisasterCodeIsManual] =
    useState(false);
  const latestDisasterTriageCodesRef = useRef({
    triageCode: episodeTriageCode,
    disasterCode: episodeDisasterCode,
    triageCodeIsManual: episodeTriageCodeIsManual,
    disasterCodeIsManual: episodeDisasterCodeIsManual,
  });
  latestDisasterTriageCodesRef.current = {
    triageCode: episodeTriageCode,
    disasterCode: episodeDisasterCode,
    triageCodeIsManual: episodeTriageCodeIsManual,
    disasterCodeIsManual: episodeDisasterCodeIsManual,
  };
  // This is the last response accepted from the episode endpoint. Comparing
  // against it keeps a code changed by another OASIS save out of this PATCH.
  const savedDisasterTriageCodesRef = useRef<{
    patientId: string;
    episodeId: string;
    triageCode: string;
    disasterCode: string;
    triageCodeIsManual: boolean;
    disasterCodeIsManual: boolean;
  } | null>(null);
  const isSavingDisasterTriageCodesRef = useRef(false);
  const pendingDisasterTriageCodesRef =
    useRef<DisasterTriageCodesSnapshot | null>(null);
  const patientIdRef = useRef(patient.id);
  const mountedRef = useRef(true);
  const [physicianName, setPhysicianName] = useState<string | null>(null);
  const [currentPhysicianId, setCurrentPhysicianId] = useState<string | null>(
    null
  );
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [associatedPhysicianIds, setAssociatedPhysicianIds] = useState<
    string[]
  >([]);
  const [episodeId, setEpisodeId] = useState<string | null>(null);
  const episodeIdRef = useRef<string | null>(null);
  const [episodeLoaded, setEpisodeLoaded] = useState(false);
  // Physician just saved to the episode, pending the "also update the NOA?"
  // popup. Non-null means the popup is open; the id is passed explicitly to the
  // sync endpoint so a concurrent reassignment by someone else can't be applied
  // to the NOA by mistake.
  const [noaSyncPhysicianId, setNoaSyncPhysicianId] = useState<string | null>(
    null
  );
  const [isSyncingNoaPhysician, setIsSyncingNoaPhysician] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCaseManagers([]);
    setCurrentCaseManagerId(null);
    setClinicalManagers([]);
    setCurrentClinicalManagerId(null);
    confirmedClinicalManagerIdRef.current = null;
    clinicalManagerRequestRef.current += 1;
    confirmedAssociatedPhysicianIdsRef.current = [];
    associatedSaveTailRef.current = Promise.resolve();
    associatedSavePendingCountRef.current = 0;
    setAssociatedSavePending(false);
    chartGenerationRef.current += 1;
    setEpisodeStartDate(null);
    setEpisodeEndDate(null);
    setEpisodeTriageCode('');
    setEpisodeDisasterCode('');
    setEpisodeTriageCodeIsManual(false);
    setEpisodeDisasterCodeIsManual(false);
    savedDisasterTriageCodesRef.current = null;
    patientIdRef.current = patient.id;
    pendingDisasterTriageCodesRef.current = null;
    setPhysicianName(null);
    setCurrentPhysicianId(null);
    setAssociatedPhysicianIds([]);
    setPhysicians([]);
    episodeIdRef.current = null;
    setEpisodeId(null);
    setEpisodeLoaded(false);

    const fetchEpisodeAndCaseManagers = async () => {
      try {
        const episodeResponse = await getCurrentEpisode({
          path: { patient_id: patient.id },
        });

        if (cancelled) return;

        if (episodeResponse?.data) {
          episodeIdRef.current = episodeResponse.data.episode_id;
          setEpisodeId(episodeResponse.data.episode_id);
          setCurrentCaseManagerId(episodeResponse.data.case_manager_id ?? null);
          confirmedClinicalManagerIdRef.current =
            episodeResponse.data.clinical_manager_id ?? null;
          setCurrentClinicalManagerId(confirmedClinicalManagerIdRef.current);
          setEpisodeStartDate(episodeResponse.data.start_date ?? null);
          setEpisodeEndDate(episodeResponse.data.end_date ?? null);
          const triageCode = episodeResponse.data.triage_code ?? '';
          const disasterCode = episodeResponse.data.disaster_code ?? '';
          const triageCodeIsManual =
            episodeResponse.data.triage_code_is_manual ?? false;
          const disasterCodeIsManual =
            episodeResponse.data.disaster_code_is_manual ?? false;
          setEpisodeTriageCode(triageCode);
          setEpisodeDisasterCode(disasterCode);
          setEpisodeTriageCodeIsManual(triageCodeIsManual);
          setEpisodeDisasterCodeIsManual(disasterCodeIsManual);
          savedDisasterTriageCodesRef.current = {
            patientId: patient.id,
            episodeId: episodeResponse.data.episode_id,
            triageCode,
            disasterCode,
            triageCodeIsManual,
            disasterCodeIsManual,
          };
          setPhysicianName(episodeResponse.data.physician_name ?? null);
          setCurrentPhysicianId(episodeResponse.data.physician_id ?? null);
          const associatedIds =
            episodeResponse.data.associated_clinic_physician_ids ?? [];
          confirmedAssociatedPhysicianIdsRef.current = associatedIds;
          associatedSaveTailRef.current = Promise.resolve();
          associatedSavePendingCountRef.current = 0;
          setAssociatedSavePending(false);
          setAssociatedPhysicianIds(associatedIds);
          const clinicId = episodeResponse.data.agency_clinic_id;
          if (clinicId) {
            const [cliniciansResponse, physiciansResponse] = await Promise.all([
              getAvailableClinicians({
                query: { clinic_id: clinicId },
              }),
              listAgencyClinicPhysicians({
                path: { agency_clinic_id: clinicId },
              }),
            ]);
            if (!cancelled && cliniciansResponse?.data) {
              setCaseManagers(
                cliniciansResponse.data.filter(c => c.is_case_manager)
              );
              setClinicalManagers(
                cliniciansResponse.data.filter(c => c.is_clinical_manager)
              );
            }
            if (!cancelled && physiciansResponse?.data) {
              setPhysicians(physiciansResponse.data);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching episode data:', err);
          showError('Unable to load episode data. Please try again.');
        }
      } finally {
        if (!cancelled) setEpisodeLoaded(true);
      }
    };

    fetchEpisodeAndCaseManagers();
    return () => {
      cancelled = true;
    };
  }, [patient.id]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const saveDisasterTriageCodes = async (
    snapshot: DisasterTriageCodesSnapshot
  ): Promise<void> => {
    if (
      !canEditPatientInfo ||
      !episodeLoaded ||
      !episodeId ||
      !snapshot.episodeId ||
      patientIdRef.current !== snapshot.patientId ||
      episodeIdRef.current !== snapshot.episodeId
    ) {
      return;
    }
    if (isSavingDisasterTriageCodesRef.current) {
      pendingDisasterTriageCodesRef.current = snapshot;
      return;
    }

    const updatesTriageCode =
      'triage_code' in snapshot.body ||
      snapshot.body.reset_triage_code_to_oasis === true;
    const updatesDisasterCode =
      'disaster_code' in snapshot.body ||
      snapshot.body.reset_disaster_code_to_oasis === true;
    const isTriageCodeCurrent = () =>
      !updatesTriageCode ||
      (latestDisasterTriageCodesRef.current.triageCode ===
        snapshot.triageCode &&
        latestDisasterTriageCodesRef.current.triageCodeIsManual ===
          snapshot.triageCodeIsManual);
    const isDisasterCodeCurrent = () =>
      !updatesDisasterCode ||
      (latestDisasterTriageCodesRef.current.disasterCode ===
        snapshot.disasterCode &&
        latestDisasterTriageCodesRef.current.disasterCodeIsManual ===
          snapshot.disasterCodeIsManual);
    const isCurrentSnapshot = () =>
      mountedRef.current &&
      patientIdRef.current === snapshot.patientId &&
      episodeIdRef.current === snapshot.episodeId &&
      isTriageCodeCurrent() &&
      isDisasterCodeCurrent();

    isSavingDisasterTriageCodesRef.current = true;
    try {
      const response =
        await updateCurrentEpisodeDisasterTriageCodesPatientsPatientIdCurrentEpisodeDisasterTriageCodesPatch(
          {
            path: { patient_id: snapshot.patientId },
            body: snapshot.body,
          }
        );
      if (response.data) {
        const saved = savedDisasterTriageCodesRef.current;
        if (
          patientIdRef.current === snapshot.patientId &&
          episodeIdRef.current === snapshot.episodeId &&
          saved?.patientId === snapshot.patientId &&
          saved.episodeId === snapshot.episodeId
        ) {
          if (updatesTriageCode) {
            saved.triageCode = response.data.triage_code ?? '';
            saved.triageCodeIsManual =
              response.data.triage_code_is_manual ?? false;
          }
          if (updatesDisasterCode) {
            saved.disasterCode = response.data.disaster_code ?? '';
            saved.disasterCodeIsManual =
              response.data.disaster_code_is_manual ?? false;
          }
        }
        // Only apply fields this request touched; the response can contain a
        // sibling changed by OASIS or a later edit while the request was open.
        if (isCurrentSnapshot()) {
          if (updatesTriageCode) {
            setEpisodeTriageCode(response.data.triage_code ?? '');
            setEpisodeTriageCodeIsManual(
              response.data.triage_code_is_manual ?? false
            );
          }
          if (updatesDisasterCode) {
            setEpisodeDisasterCode(response.data.disaster_code ?? '');
            setEpisodeDisasterCodeIsManual(
              response.data.disaster_code_is_manual ?? false
            );
          }
        }
      } else if (isCurrentSnapshot()) {
        showError(
          getUserFacingApiMessage(
            response,
            'Failed to update disaster triage codes'
          )
        );
      }
    } catch (err: unknown) {
      if (isCurrentSnapshot() && !pendingDisasterTriageCodesRef.current) {
        console.error('Failed to update disaster triage codes:', err);
        showError('Failed to update disaster triage codes');
      }
    } finally {
      isSavingDisasterTriageCodesRef.current = false;
      const pending = pendingDisasterTriageCodesRef.current;
      pendingDisasterTriageCodesRef.current = null;
      if (pending && pending.patientId === patientIdRef.current) {
        await saveDisasterTriageCodes(pending);
      }
    }
  };

  const currentDisasterTriageCodeSnapshot =
    (): DisasterTriageCodesSnapshot => ({
      patientId: patient.id,
      episodeId: episodeId ?? '',
      ...latestDisasterTriageCodesRef.current,
      body: {},
    });

  const handleDisasterTriageCodesBlur = (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    // Let the second code field's blur submit both edits after the user tabs
    // between them instead of racing two single-field PATCH requests.
    if (
      event.relatedTarget instanceof HTMLElement &&
      event.relatedTarget.dataset.disasterTriageCode === 'true'
    ) {
      return;
    }
    const { triageCode, disasterCode } = latestDisasterTriageCodesRef.current;
    const saved = savedDisasterTriageCodesRef.current;
    if (
      !saved ||
      !episodeLoaded ||
      !episodeId ||
      saved.patientId !== patient.id ||
      saved.episodeId !== episodeId ||
      episodeIdRef.current !== episodeId ||
      !canEditPatientInfo ||
      patientIdRef.current !== patient.id
    ) {
      return;
    }

    const body: DisasterTriageCodesPatchBody = {};
    const normalizedTriageCode = triageCode.trim() || null;
    const normalizedDisasterCode = disasterCode.trim() || null;
    if (triageCode !== saved.triageCode) {
      body.triage_code = normalizedTriageCode;
    }
    if (disasterCode !== saved.disasterCode) {
      body.disaster_code = normalizedDisasterCode;
    }
    if (Object.keys(body).length === 0) return;

    void saveDisasterTriageCodes({
      ...currentDisasterTriageCodeSnapshot(),
      body,
    });
  };

  const handleResetDisasterTriageCode = (field: 'triage' | 'disaster') => {
    const current = latestDisasterTriageCodesRef.current;
    if (
      !canEditPatientInfo ||
      !episodeLoaded ||
      !episodeId ||
      episodeIdRef.current !== episodeId ||
      patientIdRef.current !== patient.id
    ) {
      return;
    }
    if (field === 'triage' && !current.triageCodeIsManual) return;
    if (field === 'disaster' && !current.disasterCodeIsManual) return;

    const snapshot = currentDisasterTriageCodeSnapshot();
    snapshot.body =
      field === 'triage'
        ? { reset_triage_code_to_oasis: true }
        : { reset_disaster_code_to_oasis: true };
    void saveDisasterTriageCodes(snapshot);
  };

  const handleCaseManagerChange = async (value: string) => {
    const previousId = currentCaseManagerId;
    setCurrentCaseManagerId(value);
    try {
      const response = await updateCurrentEpisodeCaseManager({
        path: { patient_id: patient.id },
        body: { case_manager_id: value },
      });
      if (!response?.data) {
        setCurrentCaseManagerId(previousId);
        showError(
          getUserFacingApiMessage(response, 'Failed to update case manager')
        );
      }
    } catch {
      setCurrentCaseManagerId(previousId);
      showError('Failed to update case manager');
    }
  };

  // Optional, unlike the case manager: clearing the select unassigns it.
  // Changes can overlap, so each carries a token and only the latest may touch
  // the UI, and a failure restores the last value the server confirmed rather
  // than the optimistic value of a request still in flight.
  const handleClinicalManagerChange = async (value: string) => {
    const nextId = value || null;
    const token = ++clinicalManagerRequestRef.current;
    setCurrentClinicalManagerId(nextId);
    const isLatest = () => clinicalManagerRequestRef.current === token;
    const rollback = (message: string) => {
      if (!isLatest()) return;
      setCurrentClinicalManagerId(confirmedClinicalManagerIdRef.current);
      showError(message);
    };
    try {
      const response = await updateCurrentEpisodeClinicalManager({
        path: { patient_id: patient.id },
        body: { clinical_manager_id: nextId },
      });
      if (!response?.data) {
        rollback(
          getUserFacingApiMessage(response, 'Failed to update clinical manager')
        );
      } else if (isLatest()) {
        // A late answer to an older pick must not overwrite what the newer
        // save confirmed.
        confirmedClinicalManagerIdRef.current = nextId;
      }
    } catch {
      rollback('Failed to update clinical manager');
    }
  };

  const handlePhysicianChange = async (value: string) => {
    if (!episodeId) return;
    const generation = chartGenerationRef.current;
    const stillCurrentChart = () => chartGenerationRef.current === generation;
    // Drain queued associated-list writes first so a late full-list replace
    // cannot land after this promotion and put the old primary back.
    await associatedSaveTailRef.current.catch(() => undefined);
    if (!stillCurrentChart()) return;
    const previousId = currentPhysicianId;
    setCurrentPhysicianId(value);
    // Promoting to primary drops that physician from the associated list locally;
    // the server does the same when clinic_physician_id is patched alone.
    setAssociatedPhysicianIds(prev => prev.filter(id => id !== value));
    try {
      const response = await updateEpisodeAssignments({
        path: { episode_id: episodeId },
        body: { clinic_physician_id: value },
      });
      if (!stillCurrentChart()) return;
      if (!response?.data) {
        setCurrentPhysicianId(previousId);
        setAssociatedPhysicianIds(confirmedAssociatedPhysicianIdsRef.current);
        showError('Failed to update physician');
      } else {
        // Filter the live confirmed ref so a completed associated add that
        // landed while this save was in flight is not wiped.
        confirmedAssociatedPhysicianIdsRef.current =
          confirmedAssociatedPhysicianIdsRef.current.filter(id => id !== value);
        setAssociatedPhysicianIds(confirmedAssociatedPhysicianIdsRef.current);
        onEpisodeAssignmentsChanged?.();
        // Episode saved — offer to carry the same physician onto the NOA, but
        // only when there is an unsent NOA whose physician actually differs.
        // A failed check just skips the popup; the episode save already stuck.
        try {
          const check = await checkEpisodeNoaPhysicianSync({
            path: { episode_id: episodeId },
            query: { clinic_physician_id: value },
          });
          if (stillCurrentChart() && check.data?.needs_update) {
            setNoaSyncPhysicianId(value);
          }
        } catch (err: unknown) {
          console.error('Failed to check NOA physician sync:', err);
        }
      }
    } catch {
      if (!stillCurrentChart()) return;
      setCurrentPhysicianId(previousId);
      setAssociatedPhysicianIds(confirmedAssociatedPhysicianIdsRef.current);
      showError('Failed to update physician');
    }
  };

  // Serialize full-list replaces: each mutate sees the last confirmed list,
  // waits for the prior save, and holds the controls disabled while in flight.
  const persistAssociatedPhysicians = (
    mutate: (current: string[]) => string[]
  ) => {
    if (!episodeId) return Promise.resolve();
    const episodeIdForSave = episodeId;
    const generation = chartGenerationRef.current;
    const stillCurrentChart = () => chartGenerationRef.current === generation;
    const run = async () => {
      if (!stillCurrentChart()) return;
      const previousIds = confirmedAssociatedPhysicianIdsRef.current;
      const nextIds = mutate(previousIds);
      if (
        nextIds.length === previousIds.length &&
        nextIds.every((id, index) => id === previousIds[index])
      ) {
        return;
      }
      associatedSavePendingCountRef.current += 1;
      setAssociatedSavePending(true);
      setAssociatedPhysicianIds(nextIds);
      try {
        const response = await updateEpisodeAssignments({
          path: { episode_id: episodeIdForSave },
          body: { associated_clinic_physician_ids: nextIds },
        });
        if (!stillCurrentChart()) return;
        if (!response?.data) {
          setAssociatedPhysicianIds(confirmedAssociatedPhysicianIdsRef.current);
          showError('Failed to update associated physicians');
          return;
        }
        confirmedAssociatedPhysicianIdsRef.current = nextIds;
        setAssociatedPhysicianIds(nextIds);
        onEpisodeAssignmentsChanged?.();
      } catch {
        if (!stillCurrentChart()) return;
        setAssociatedPhysicianIds(confirmedAssociatedPhysicianIdsRef.current);
        showError('Failed to update associated physicians');
      } finally {
        // Patient change resets the counter; do not drive it negative or flip
        // the new chart's pending flag from this stale job.
        if (stillCurrentChart()) {
          associatedSavePendingCountRef.current -= 1;
          if (associatedSavePendingCountRef.current === 0) {
            setAssociatedSavePending(false);
          }
        }
      }
    };
    const next = associatedSaveTailRef.current.then(run, run);
    associatedSaveTailRef.current = next.then(
      () => undefined,
      () => undefined
    );
    return next;
  };

  const handleAddAssociatedPhysician = (physicianId: string) => {
    if (!physicianId || !episodeId || physicianId === currentPhysicianId) {
      return;
    }
    // Do not gate on associatedSavePending here: a second click can land
    // before React re-renders the disabled controls. The save queue serializes.
    void persistAssociatedPhysicians(current => {
      if (
        current.includes(physicianId) ||
        current.length >= MAX_ASSOCIATED_PHYSICIANS
      ) {
        return current;
      }
      return [...current, physicianId];
    });
  };

  const handleRemoveAssociatedPhysician = (physicianId: string) => {
    void persistAssociatedPhysicians(current =>
      current.filter(id => id !== physicianId)
    );
  };

  const handleConfirmNoaPhysicianSync = async () => {
    if (!episodeId || !noaSyncPhysicianId) return;
    setIsSyncingNoaPhysician(true);
    try {
      const response = await syncEpisodeNoaPhysician({
        path: { episode_id: episodeId },
        body: { clinic_physician_id: noaSyncPhysicianId },
      });
      if (response.error || !response.data) {
        console.error('Failed to update NOA physician:', response.error);
        showError('Failed to update the NOA physician');
      } else if (response.data.updated) {
        showSuccess('NOA physician updated');
      } else {
        // Race guard: between the check and this confirm, the NOA may have
        // been sent or already updated to this physician by someone else.
        showSuccess('NOA already up to date — nothing was changed');
      }
    } catch (err: unknown) {
      console.error('Failed to update NOA physician:', err);
      showError('Failed to update the NOA physician');
    } finally {
      setIsSyncingNoaPhysician(false);
      setNoaSyncPhysicianId(null);
    }
  };

  // Build physician dropdown options from the clinic roster. If the episode's
  // current physician isn't in the active roster (e.g. soft-deleted), prepend a
  // synthetic option so the assignment still renders rather than going blank.
  const physicianOptions = physicians.map(p => ({
    value: p.id,
    label: formatPhysicianName(p),
  }));
  if (
    currentPhysicianId &&
    !physicianOptions.some(o => o.value === currentPhysicianId)
  ) {
    physicianOptions.unshift({
      value: currentPhysicianId,
      label: physicianName ?? 'Current physician',
    });
  }

  const associatedPhysicianOptions = physicians
    .filter(
      p => p.id !== currentPhysicianId && !associatedPhysicianIds.includes(p.id)
    )
    .map(p => ({
      value: p.id,
      label: formatPhysicianName(p),
    }));

  const associatedPhysicians = associatedPhysicianIds.map(id => {
    const roster = physicians.find(p => p.id === id) ?? null;
    return { id, physician: roster };
  });

  // The roster entry behind the current assignment, for the details popover.
  // Stays null for the synthetic option above: a physician off the roster has
  // no record to read metadata from, so the popover renders nothing.
  const selectedPhysician = useMemo(
    () => physicians.find(p => p.id === currentPhysicianId) ?? null,
    [physicians, currentPhysicianId]
  );

  return (
    <>
      {/* Current Episode */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <MapPin className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Current Episode</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <div className='space-y-1.5'>
              <Label>Case Manager</Label>
              <ClinicianSelect
                key={patient.id}
                label=''
                selectSize='sm'
                placeholder={
                  !episodeLoaded ? 'Loading...' : 'Select case manager'
                }
                clinicians={caseManagers}
                defaultValue={currentCaseManagerId ?? ''}
                disabled={!episodeLoaded}
                onChange={e => handleCaseManagerChange(e.target.value)}
                isClearable={false}
                menuPosition='absolute'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Clinical Manager</Label>
              <ClinicianSelect
                key={patient.id}
                label=''
                selectSize='sm'
                placeholder={!episodeLoaded ? 'Loading...' : 'Unassigned'}
                clinicians={clinicalManagers}
                // Controlled, so a rollback after a failed save moves the display
                // back too; defaultValue is only read when the select mounts.
                value={currentClinicalManagerId ?? ''}
                disabled={!episodeLoaded}
                onChange={e => handleClinicalManagerChange(e.target.value)}
                isClearable
                menuPosition='absolute'
              />
            </div>
            <div className='space-y-1.5'>
              {/* h-6 matches the line box a bare inline Label occupies in the
                  sibling cells, so making room for the icon does not lift this
                  column's picker above the others. */}
              <div className='flex h-6 items-center gap-1'>
                <Label>Primary physician</Label>
                <PhysicianInfoPopover physician={selectedPhysician} />
              </div>
              <VirtualizedFormSelect
                key={patient.id}
                label=''
                selectSize='sm'
                placeholder={!episodeLoaded ? 'Loading...' : 'Select physician'}
                options={physicianOptions}
                sortOptions
                defaultValue={currentPhysicianId ?? ''}
                disabled={!episodeLoaded}
                onChange={e => handlePhysicianChange(e.target.value)}
                isClearable={false}
                menuPosition='absolute'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode Start Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeStartDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode End Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeEndDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
            <div className='space-y-1.5'>
              <div className='flex items-center justify-between gap-2'>
                <Label htmlFor='episode-triage-code'>Triage / Risk Code</Label>
                {episodeTriageCodeIsManual && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    aria-label='Use OASIS for Triage / Risk Code'
                    disabled={
                      !canEditPatientInfo || !episodeLoaded || !episodeId
                    }
                    onClick={() => handleResetDisasterTriageCode('triage')}
                  >
                    Use OASIS
                  </Button>
                )}
              </div>
              <Input
                id='episode-triage-code'
                data-disaster-triage-code='true'
                value={episodeLoaded ? episodeTriageCode : 'Loading...'}
                onChange={e => setEpisodeTriageCode(e.target.value)}
                onBlur={handleDisasterTriageCodesBlur}
                disabled={!canEditPatientInfo || !episodeLoaded || !episodeId}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <div className='flex items-center justify-between gap-2'>
                <Label htmlFor='episode-disaster-code'>Disaster Code</Label>
                {episodeDisasterCodeIsManual && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    aria-label='Use OASIS for Disaster Code'
                    disabled={
                      !canEditPatientInfo || !episodeLoaded || !episodeId
                    }
                    onClick={() => handleResetDisasterTriageCode('disaster')}
                  >
                    Use OASIS
                  </Button>
                )}
              </div>
              <Input
                id='episode-disaster-code'
                data-disaster-triage-code='true'
                value={episodeLoaded ? episodeDisasterCode : 'Loading...'}
                onChange={e => setEpisodeDisasterCode(e.target.value)}
                onBlur={handleDisasterTriageCodesBlur}
                disabled={!canEditPatientInfo || !episodeLoaded || !episodeId}
                inputSize='sm'
              />
            </div>
          </div>

          <div className='mt-4 space-y-2 border-t pt-4'>
            <div className='flex items-center justify-between gap-2'>
              <Label>Associated physicians</Label>
              <span className='text-xs text-muted-foreground'>
                {associatedPhysicianIds.length}/{MAX_ASSOCIATED_PHYSICIANS}
              </span>
            </div>
            {associatedPhysicians.length > 0 ? (
              <ul className='space-y-2'>
                {associatedPhysicians.map(({ id, physician }) => (
                  <li
                    key={id}
                    className='flex items-center justify-between gap-2'
                  >
                    <div className='flex min-w-0 items-center gap-1'>
                      <span className='truncate text-sm'>
                        {physician
                          ? formatPhysicianName(physician)
                          : 'Physician'}
                      </span>
                      <PhysicianInfoPopover physician={physician} />
                    </div>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 w-8 shrink-0 p-0'
                      aria-label={`Remove ${
                        physician ? formatPhysicianName(physician) : 'physician'
                      }`}
                      disabled={!episodeLoaded || associatedSavePending}
                      onClick={() => handleRemoveAssociatedPhysician(id)}
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className='text-sm text-muted-foreground'>
                {episodeLoaded ? 'No associated physicians yet.' : 'Loading...'}
              </p>
            )}
            {episodeLoaded &&
              associatedPhysicianIds.length < MAX_ASSOCIATED_PHYSICIANS && (
                <div className='flex items-center gap-2 pt-1'>
                  <Plus className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <div className='min-w-0 flex-1'>
                    <VirtualizedFormSelect
                      key={`assoc-add-${associatedPhysicianIds.join(',')}-${currentPhysicianId ?? ''}`}
                      label=''
                      selectSize='sm'
                      placeholder='Add associated physician'
                      options={associatedPhysicianOptions}
                      sortOptions
                      defaultValue=''
                      disabled={
                        associatedPhysicianOptions.length === 0 ||
                        associatedSavePending
                      }
                      onChange={e => {
                        const value = e.target.value;
                        if (value) {
                          handleAddAssociatedPhysician(value);
                        }
                      }}
                      isClearable={false}
                      menuPosition='absolute'
                    />
                  </div>
                </div>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Imported Chart — only on imported charts; the card itself is the
          residual indicator after the badge is demoted */}
      {patient.import_source && (
        <PatientImportBadgeToggle
          patientId={patient.id}
          importedAt={patient.imported_at}
          badgeHidden={!!patient.import_badge_hidden_at}
          onUpdated={onPatientUpdated}
        />
      )}

      {/* Test / Practice Patient — last section; explains itself before the toggle */}
      <PatientTestStatusToggle
        patientId={patient.id}
        isTest={!!patient.is_test_patient}
        onUpdated={onPatientUpdated}
      />

      {/* Offered after a physician save; only an unsent NOA is ever changed */}
      <ConfirmationDialog
        open={!!noaSyncPhysicianId}
        title='Update NOA physician?'
        message="Also update the physician on this patient's Notice of Admission? A NOA that has already been sent will not be changed."
        confirmLabel='Yes, update NOA'
        cancelLabel='No'
        loading={isSyncingNoaPhysician}
        onAccept={() => void handleConfirmNoaPhysicianSync()}
        onCancel={() => setNoaSyncPhysicianId(null)}
      />
    </>
  );
};
